import { useMemo, useState, type FormEvent } from "react";
import { AlertCircle } from "lucide-react";
import HoneypotField from "./HoneypotField";
import EnquiryConfirmation from "./EnquiryConfirmation";
import useEnquiryForm from "../hooks/useEnquiryForm";
import { submitFranchiseEnquiry } from "../utils/franchiseEnquiry";
import { SERVICE_ZONES, EXPANSION_CITIES } from "../data/operations";

const INVESTMENT_RANGES = ["Under ₹25L", "₹25L–₹50L", "₹50L–₹1Cr", "₹1Cr+"];

export default function FranchiseEnquiryForm() {
  const { status, referenceNumber, submit, reset } = useEnquiryForm("franchise");
  const [honeypot, setHoneypot] = useState("");

  const [applicantName, setApplicantName] = useState("");
  const [company, setCompany] = useState("");
  const [targetCity, setTargetCity] = useState("");
  const [investmentRange, setInvestmentRange] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  // Real lookup against operations.ts's actual served-city list — not
  // fabricated. The "alternative" suggestion pulls from EXPANSION_CITIES,
  // ZenFresh's own genuine future-target list, which is the semantically
  // correct answer to "where should a franchise partner look instead," not
  // just the nearest random city.
  const cityStatus = useMemo(() => {
    const trimmed = targetCity.trim().toLowerCase();
    if (!trimmed) return null;
    const alreadyServed = SERVICE_ZONES.some((z) => z.cityName.toLowerCase() === trimmed);
    if (!alreadyServed) return null;
    const suggestion = EXPANSION_CITIES[0];
    return { alreadyServed, suggestion };
  }, [targetCity]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    submit(() =>
      submitFranchiseEnquiry({ applicantName, company, targetCity, investmentRange, contactEmail })
    );
  };

  if (status === "success" && referenceNumber) {
    return <EnquiryConfirmation type="franchise" referenceNumber={referenceNumber} onSendAnother={reset} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <HoneypotField value={honeypot} onChange={setHoneypot} />

      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Your name</span>
          <input
            required
            type="text"
            value={applicantName}
            onChange={(e) => setApplicantName(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Company / background</span>
          <input
            required
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Target city</span>
          <input
            required
            type="text"
            value={targetCity}
            onChange={(e) => setTargetCity(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Investment capacity</span>
          <select
            required
            value={investmentRange}
            onChange={(e) => setInvestmentRange(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          >
            <option value="" disabled>
              Select a range
            </option>
            {INVESTMENT_RANGES.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="font-medium text-ocean-900 dark:text-foam-100">Email</span>
          <input
            required
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-ocean-900/10 bg-foam-50 px-4 py-2.5 text-ocean-950 outline-none transition-colors focus:border-teal-400 dark:border-white/10 dark:bg-white/5 dark:text-foam-50"
          />
        </label>
      </div>

      {cityStatus?.alreadyServed && (
        <div role="alert" className="flex items-start gap-3 rounded-xl bg-coral-500/10 p-4 text-sm text-coral-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            ZenFresh already delivers in {targetCity} — we prioritize franchise conversations in cities we
            haven't launched in yet.
            {cityStatus.suggestion && (
              <> Consider proposing <strong>{cityStatus.suggestion.cityName}</strong>, one of our expansion targets, instead.</>
            )}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-full bg-teal-400 py-3 text-sm font-semibold text-ocean-950 transition-colors hover:bg-teal-300 disabled:opacity-50"
      >
        {status === "loading" ? "Submitting…" : "Submit Enquiry"}
      </button>
      {status === "error" && <p className="text-center text-xs text-coral-500">Something went wrong — please try again.</p>}
    </form>
  );
}
